import { Close } from "@mui/icons-material";
import { Backdrop, Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import BriaCanvas from "../../../components/common/Galleries/BriaCanvas";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { usePostHog } from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores";
import MaskEditorControlsRow from "./ControlPanel/ControlPanel";
import styles from "./MaskEditor.module.scss";

interface MaskEditorHeaderProps {
	onClose?: () => void;
}

const MaskEditorHeader = ({ onClose }: MaskEditorHeaderProps) => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.maskEditor" });

	return (
		<Box className={styles.header}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<BriaButton
				className={styles.closeButton}
				startIcon={<Close />}
				buttonType="tertiaryMedium"
				onClick={onClose}
			>
				{t("close")}
			</BriaButton>
		</Box>
	);
};

export interface MaskEditorProps {
	open: boolean;
	image: string;

	onClose?: () => void;
}

const MaskEditor = ({ open, image, onClose }: MaskEditorProps) => {
	const { fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const maskEditorStore = fiboStore.fiboEditStore.maskEditorStore;
	const { trackEvent } = usePostHog();

	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const canvasOverlayRef = useRef<HTMLCanvasElement | null>(null);
	const maskLoadedForSessionRef = useRef(false);

	useEffect(() => {
		maskEditorStore.brushCanvasRefs[0] = {
			canvasRef,
			canvasOverlayRef,
		};

		return () => maskEditorStore.resetBrushConfigs();
	}, []);

	// Try to load mask when editor opens or canvas becomes ready
	const tryLoadMask = async () => {
		if (open && fiboEditStore.config.mask && !maskLoadedForSessionRef.current) {
			await maskEditorStore.loadMaskOntoCanvas(fiboEditStore.config.mask);
			maskLoadedForSessionRef.current = true;
		}
	};

	useEffect(() => {
		maskEditorStore.isEditorOpened = open;
		if (open) {
			maskLoadedForSessionRef.current = false; // Reset for new session
			tryLoadMask();
			trackEvent(POSTHOG_EVENTS.FIBO_MASK_EDITOR_OPENED, {
				lab_variant: "FIBO-EDIT",
			});
		}
	}, [open, trackEvent]);

	// Set original image dimensions for proper mask scaling
	useEffect(() => {
		if (image) {
			maskEditorStore.setOriginalImageDimensions(image);
		}
	}, [image]);

	const handleClose = () => {
		maskEditorStore.isEditorOpened = false;

		if (!maskEditorStore.isMaskEditing) {
			// Don't reset the brush configs if the mask is being edited
			maskEditorStore.resetBrushConfigs();
		} else {
			maskEditorStore.isMaskEditing = false;
		}

		maskLoadedForSessionRef.current = false;
		onClose?.();
	};

	const handleUseMask = async () => {
		const maskFileBase64 = await maskEditorStore.getMaskFileBase64();
		if (maskFileBase64) fiboStore.fiboEditStore.config.mask = maskFileBase64;
		fiboStore.fiboEditStore.openMaskEditor = false;
		trackEvent(POSTHOG_EVENTS.FIBO_MASK_EDITOR_MASK_APPLIED, {
			lab_variant: "FIBO-EDIT",
		});
	};

	return (
		<Backdrop open={maskEditorStore.isEditorOpened} className={styles.backdrop}>
			<Box className={styles.maskEditorContent}>
				<MaskEditorHeader onClose={handleClose} />
				<BriaCanvas
					className={styles.canvasContainer}
					url={image}
					canvasRef={(ref: HTMLCanvasElement | null) => {
						canvasRef.current = ref;
					}}
					canvasOverlayRef={(ref: HTMLCanvasElement | null) => {
						canvasOverlayRef.current = ref;
					}}
					brushConfigs={maskEditorStore.brushConfigs}
					onCanvasSizeUpdate={() => {}}
				/>

				<MaskEditorControlsRow
					brush={{
						size: maskEditorStore.brushSize,
						type: maskEditorStore.maskType,
						onChange: (size, type) => {
							maskEditorStore.setBrushSize(size);
							maskEditorStore.setMaskType(type);
						},
					}}
					onResetMask={maskEditorStore.resetBrushConfigs}
					onUseMask={handleUseMask}
				/>
			</Box>
		</Backdrop>
	);
};

export default observer(MaskEditor);
